import { useEffect, useRef, useState } from 'react';
import { Image, Flex, Box, CircularProgress, Text, Input, } from "@chakra-ui/react";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import ImageUploading from 'react-images-uploading';
import { RiErrorWarningLine } from "react-icons/ri";
import { FaRegImages } from 'react-icons/fa6';
import FileUploadProgress from '../../../dsr-create-post/FileUploadProgress';
import config from "../../../../../config/app-config";
import LaunchdeckButton from '../../../../global/components/helper-components/components/LaunchdeckButton';
import { MdOutlineImageNotSupported } from "react-icons/md";
import { FaRegPaste } from 'react-icons/fa6';


export const UploadedImages = ({
  dsrId,
  subWidgetId,
  setSubWidgetContent,
  subWidgetContent,
  uploadFiles,
  uploadFilesState,
  uploadFilesReset,
  handleSaveItemByClickOutside,
  subWidgetAutoSave,
  isDeleting
}) => {
  const { results, error } = uploadFilesState;
  const { maxImageFileUploadLimit, maxImageFileSize, acceptedImageFileTypes } = config;

  const [images, setImages] = useState(null);
  const containerRef = useRef(null);

  const [isPasteImageMode, setIsPasteImageMode] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [pasteErrorMessage, setPasteErrorMessage] = useState('');

  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processingSubwidgetId, setProcessingSubwidgetId] = useState("")
  const [showLoadingState, setShowLoadingState] = useState(false);

  const maxSingleFileSizeInMb = Math.trunc(maxImageFileSize / (1024 * 1024));
  const maxNumber = maxImageFileUploadLimit;
  const maxFileSize = maxImageFileSize;

  const configs = {
    autoPlay: false,
    infiniteLoop: true,
    showThumbs: false,
    swipeable: true,
    showStatus: false,
    showIndicators: true,
    showArrows: true,
    dynamicHeight: false,
  };

  useEffect(() => {
    if (!error && results.files && results.files[0].subWidgetId === subWidgetId) {
      setSubWidgetContent(results.files)
      setProcessingSubwidgetId("")
      uploadFilesReset();
      setShowLoadingState(false)
    };
  }, [results]);

  useEffect(() => {
    if (error && showLoadingState) {
      setShowLoadingState(false)
    }
  }, [error, showLoadingState]);

  useEffect(() => {
    //Auto save sub-widget content to local state after uploading
    if (!showLoadingState && !error && subWidgetContent[0]?.key) {
      subWidgetAutoSave()
      setIsFocused(false)
    }
  }, [subWidgetContent]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if ((isFocused && containerRef.current) && !containerRef.current.contains(event.target)) {
        setIsFocused(false);
        handleSaveItemByClickOutside();
      }
    };

    if (isFocused) {
      setTimeout(() => window.addEventListener('mousedown', handleClickOutside), 1)
    } else {
      window.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFocused]);

  const onChange = (imageList) => {
    const filesArray = imageList.map((imgData) => {
      return imgData.file
    })
    //Set images for uploading preview
    setImages(imageList);
    setProcessingSubwidgetId(subWidgetId)
    //Upload files to server
    uploadFiles({ dsrId: dsrId, files: filesArray, subWidgetId: subWidgetId, containsImages: true });
    setShowLoadingState(true)
  };

  const handleImageLoad = () => {
    setLoading(false);
  };


  const handlePaste = (event) => {
    // Access clipboard items
    const items = event.clipboardData.items;
    let file = null;

    // Loop through clipboard items to find an image
    for (const item of items) {
      if (item.type.startsWith('image')) {
        file = item.getAsFile(); // Get image file
        break;
      }
    }

    // If no image was found in clipboard data
    if (!file) {
      setPasteErrorMessage('No images found in your copy clipboard');
      return;
    }

    // Validate the image file type
    if (!file.type.match('image.*')) {
      setPasteErrorMessage('Only JPEG, JPG or PNG files are allowed');
      return;
    }

    setPasteErrorMessage(''); // Clear any previous error messages

    // Create a URL for the image to be rendered locally
    const imageUrl = URL.createObjectURL(file);

    setProcessingSubwidgetId(subWidgetId)
    setImageSrc(imageUrl); // Set image source to be rendered


    //Upload files to server
    (dsrId && subWidgetId && file) && uploadFiles({ dsrId: dsrId, files: [file], subWidgetId: subWidgetId, containsImages: true });
    setShowLoadingState(true)
  };

  if (error) {
    // TODO: Make this a reuseable component for other file retrievable
    return <Box border='1px solid' borderColor='gray.100' rounded='0.25em' w='100%' minH='5em'>
      <Flex direction='column' justify='center' align='center' m='1em auto'>
        <Box as={MdOutlineImageNotSupported} boxSize="1.5em" color="brandDark.500" />
        <Box fontSize='0.8em' color='gray.500' mt='1em' fontStyle='italic'>Uh oh, this image cannot be displayed</Box>
      </Flex>
    </Box>
  }

  return (
    isDeleting ?
      <Flex w='100%' justify='center' align='center' mr='3.5em' mt='2em' minH='8em'>
        <Box className="removing-subwidget" />
      </Flex>
      :
      subWidgetContent.length > 1
        ? (
          <Carousel
            {...configs}
          >
            {subWidgetContent?.map((image, index) => (
              <div key={index} style={{ position: 'relative' }} className="carousel-container">
                <img
                  src={image['url']} alt={""}
                  className="carousel-image"
                />
              </div>
            ))}
          </Carousel>
        )
        : subWidgetContent
          ?
          <Box position="relative" minH={loading && '18em'} border={!loading && '1px solid'} borderColor='gray.100' rounded='0.25em'>
            {loading && (
              <CircularProgress
                isIndeterminate
                color="brand.500"
                thickness='0.3em'
                size='1.5em'
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
              />
            )}
            <Image
              onLoad={handleImageLoad}
              src={subWidgetContent[0].url}
              alt="Sub-widget image"
              width="100%"
              rounded='0.25em'
              display={loading ? 'none' : 'block'}
            />
          </Box>
          :
          (
            <Flex ref={containerRef} flexDirection="column" justifyContent="center" alignItems="center" w='100%' mx='1em'>
              {(!showLoadingState || (processingSubwidgetId !== subWidgetId)) &&
                <ImageUploading
                  multiple
                  maxFileSize={maxFileSize}
                  value={images}
                  onChange={onChange}
                  acceptType={acceptedImageFileTypes}
                  maxNumber={maxNumber}
                  dataURLKey="data_url"
                >
                  {({
                    imageList,
                    onImageUpload,
                    errors,
                    isDragging,
                    dragProps,
                  }) => (
                    <div className="upload__image-wrapper">
                      {imageList.length === 0
                        ?
                        <Box p='1em'>
                          {isPasteImageMode ?
                            <Box
                              border='dashed 1px #29ABE2'
                              borderRadius="md"
                              p='1em 1em'
                              textAlign="center"
                              onPaste={handlePaste}
                            >
                              <Flex align='center' justify='space-between' gap={6}>
                                <Box as={FaRegPaste} />
                                <Input
                                  placeholder='Click here and paste an image...'
                                  variant="filled"
                                  readOnly
                                  onPaste={handlePaste}
                                  mb='0.2em'
                                  w='100%'
                                  h='1.4em'
                                  fontSize='0.875em'
                                  fontWeight='400'
                                  color='brandDark.500'
                                  border='1px solid #A0AEC0'
                                  borderRadius='0.25em'
                                  padding='1em'
                                />
                              </Flex>

                              {pasteErrorMessage && <Text color="red.500" fontSize='0.7em'>{pasteErrorMessage}</Text>}
                              {imageSrc && (
                                <Box mt={4} w='100%'>
                                  <Image src={imageSrc} alt="Pasted image" maxW="100%" maxH="300px" />
                                </Box>
                              )}

                              <Flex direction='column' align='center' gap={4} mt='1em'>
                                <LaunchdeckButton bg='gray.300' color='brandDark.500' px='2em' label='Back' onClick={() => {
                                  setPasteErrorMessage("")
                                  setIsPasteImageMode(!isPasteImageMode)
                                }} />
                              </Flex>
                            </Box>
                            :
                            <button
                              className="drop-area"
                              style={isDragging ? { color: 'red' } : undefined}
                              onClick={onImageUpload}
                              {...dragProps}
                            >

                              <Flex ref={containerRef} justifyContent="center" alignItems="center" direction="column" p='5px' m='1em'>
                                <Box fill='brandDark.500' as={FaRegImages} width="3em" height="3em" my="2em" />
                              </Flex>



                              <Flex justify='space-around' align='center' gap={16} fontSize='0.75em'>
                                <Box>
                                  <Box mt='0.75em' color='gray.500' textAlign='center'>
                                    Max images
                                  </Box>
                                  <Box textAlign='center' mt='0.5em' fontWeight='600'>
                                    {maxImageFileUploadLimit}
                                  </Box>
                                </Box>

                                <Box>
                                  <Box mt='0.75em' color='gray.500' textAlign='center'>
                                    Max size
                                  </Box>
                                  <Box textAlign='center' mt='0.5em' fontWeight='600'>
                                    {maxSingleFileSizeInMb}MB each
                                  </Box>

                                </Box>


                                <Box>

                                  <Box mt='0.75em' color='gray.500' textAlign='center'>
                                    Formats
                                  </Box>
                                  <Box textAlign='center' mt='0.5em' fontWeight='600'>
                                    JPEG, JPG, PNG
                                  </Box>


                                </Box>
                              </Flex>
                            </button>
                          }

                          {isPasteImageMode ?
                            <Flex direction='column' align='center' gap={4} my='1em'>
                            </Flex>
                            :
                            <Flex direction='column' align='center' gap={4} my='1em'>
                              <LaunchdeckButton bg='brandDark.500' color='white' px='2em' label='Select image' onClick={onImageUpload} />
                              <LaunchdeckButton bg='gray.300' color='brandDark.500' px='2em' label='Paste image' onClick={() => setIsPasteImageMode(!isPasteImageMode)} />
                            </Flex>
                          }


                        </Box>
                        : imageList.length > 0
                        &&
                        <div>
                          <Carousel
                            {...configs}
                          >
                            {imageList.map((image, index) => (
                              <div key={index} style={{ position: 'relative' }} className="carousel-container">
                                <img
                                  src={image['data_url']} alt={""}
                                  className="carousel-image"
                                />
                              </div>
                            ))}
                          </Carousel>
                        </div>
                      }
                      {errors &&
                        <div className='error-list'>
                          {errors.maxNumber ? <><RiErrorWarningLine color="brandPink.500" boxSize={20} fontSize='0.875em' /> &nbsp; Only 5 images can be uploaded at any one time </> : errors.maxFileSize ? <><RiErrorWarningLine
                            color="red" boxSize={20} /> &nbsp; Upload limit is 20 MB per image</> : ""}
                        </div>
                      }
                    </div>
                  )}
                </ImageUploading>
              }
              {showLoadingState && (processingSubwidgetId === subWidgetId) &&
                <Flex w='100%' py='2em' justify='center'>
                  <FileUploadProgress showLoader={showLoadingState} subWidgetId={subWidgetId} />
                </Flex>
              }
            </Flex>
          )
  );
};

